import {useAnimations} from '@react-three/drei'
import {ThreeElements, useFrame, useLoader} from '@react-three/fiber'
import AudioManager from '@repo/shared/AudioManager'
import {ESprintenTeam} from '@repo/shared/Customer/Coop/SprintenTeams/SprintenTeams.types'
import gsap from 'gsap'
import {damp3} from 'maath/easing'
import {useEffect, useRef, useState} from 'react'
import {Vector3} from 'three'
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader'
import {IEndPositionsState} from '../Store/atoms'
import FrameStore, {FINISH_LINE_X_OFFSET_NEG} from '../Store/FrameStore'
import {EAction, IActions, PlayerHandler} from './PlayerHandler'

const moveToVector = new Vector3()
const fStore = new FrameStore()
const audioManager = new AudioManager()

export const Player = ({
  slot,
  refrencePoints,
  teamName,
}: {
  slot: number
  refrencePoints: {
    [key: string]: Vector3
  }
  teamName: ESprintenTeam
}) => {
  const {nodes, materials, animations, scene} = useLoader(
    GLTFLoader,
    `/models/players/player_${teamName}.glb`,
  )
  // const {nodes, animations, scene} = useLoader(GLTFLoader, `/models/players/player_extra.glb`)

  const {actions} = useAnimations(animations, scene)
  const playerHandler = useRef(PlayerHandler(actions as unknown as IActions, teamName))

  useEffect(() => {
    if (scene) {
      // correct the rotation of the player
      scene.rotation.y = Math.PI * 0.5
    }

    playerHandler.current.init()

    if (actions) {
      playerHandler.current.fadeToAction(EAction.Idle, 0.5)
    }
  }, [scene])

  // get the start and end position for the player
  const startPos = refrencePoints[`ref_point_team_${slot}_start`] ?? new Vector3(0, -1000, 0)
  const endPos = refrencePoints[`ref_point_team_${slot}_end`] ?? new Vector3(0, -1000, 0)

  // create a ref for the mesh
  const meshRef = useRef<any>(null!)
  const modelRef = useRef<ThreeElements['primitive']>()
  const [hasTeam, setHasTeam] = useState(false)

  // Finish routine
  const didFinish = useRef(false)
  const didCrossFinishLine = useRef(false)
  const runFinishRoutine = (endPosition: IEndPositionsState) => {
    didFinish.current = true
    playerHandler.current.setFinished(slot, endPosition.position ?? 1)
  }

  // update the player
  useFrame((_state, delta) => {
    if (!meshRef.current) return

    // get the percentage from the leaderboard
    const currentTeam = fStore.getLeaderboard()?.leaderboardTeams.find(team => team.slot === slot)

    // check for end message
    const endMessage = fStore.getSlotEndPosition(slot)

    if (currentTeam) {
      moveToVector.set(
        gsap.utils.interpolate(
          startPos.x,
          fStore.getPathEndOffsetVec().x,
          currentTeam.percentage / 100,
        ),
        startPos.y,
        startPos.z,
      )
      damp3(meshRef.current.position, moveToVector, 1.2, delta)

      if (!hasTeam) {
        setHasTeam(true)
      }
    }

    // save the slot vec

    const meshDidCrossFinishLine = meshRef.current.position.x >= fStore.getPathEndVec().x
    const meshDidCrossFinishLineOffset =
      meshRef.current.position.x >= fStore.getPathEndVec().x - FINISH_LINE_X_OFFSET_NEG

    // Set the race animation speed
    if (!didFinish.current) {
      fStore.setTeamSlotVec(slot, meshRef.current.position)
      playerHandler.current.setRaceSpeed(playerHandler.current.getSpeed(meshRef.current.position.x))
    }
    // check if the player has crossed the finish line
    if (endMessage && !didFinish.current && meshDidCrossFinishLine) {
      runFinishRoutine(endMessage)
    }

    if (meshDidCrossFinishLineOffset && !didCrossFinishLine.current) {
      didCrossFinishLine.current = true

      if (endMessage?.position === 1) {
        audioManager.SFX.finish1?.seek(0)
        audioManager.SFX.finish1?.play()
      } else if (endMessage?.position === 2) {
        audioManager.SFX.finish3?.seek(0)
        audioManager.SFX.finish2?.play()
      } else if (endMessage?.position === 3) {
        audioManager.SFX.finish3?.seek(0)
        audioManager.SFX.finish3?.play()
      } else {
        audioManager.SFX.finish4?.seek(0)
        audioManager.SFX.finish4?.play()
      }
    }
  })

  return (
    <mesh
      ref={meshRef}
      position-x={startPos.x}
      position-y={startPos.y}
      position-z={startPos.z}
      visible={hasTeam}
    >
      <primitive ref={modelRef} object={scene} />
    </mesh>
  )
}
